import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, Building2, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function InviteAccept() {
  const { token } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loadingInvite, setLoadingInvite] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get(`/business-accounts/invites/${token}`)
      .then((r) => { if (mounted) setInvite(r.data); })
      .catch((e) => {
        if (mounted) setError(formatApiError(e?.response?.data?.detail) || "Invite is not valid.");
      })
      .finally(() => { if (mounted) setLoadingInvite(false); });
    return () => { mounted = false; };
  }, [token]);

  const acceptInvite = async () => {
    setAccepting(true);
    try {
      await api.post(`/business-accounts/invites/${token}/accept`);
      toast.success(`Joined ${invite.business_name}`);
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not accept invite");
    } finally {
      setAccepting(false);
    }
  };

  if (loadingInvite || loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#059669]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center" data-testid="invite-error">
        <Building2 className="w-12 h-12 mx-auto text-[#DC2626] mb-3" />
        <h1 className="font-display font-black text-3xl mb-2">Invite unavailable</h1>
        <p className="text-[#525252] mb-6">{error}</p>
        <Link to="/" className="text-[10px] uppercase tracking-[0.2em] font-bold border-2 border-[#0A0A0A] px-6 py-3 hover:bg-[#0A0A0A] hover:text-white">
          Back to home
        </Link>
      </div>
    );
  }

  const emailMatches = user && user.email?.toLowerCase() === invite.email?.toLowerCase();

  return (
    <div className="max-w-md mx-auto px-4 py-16 sm:py-20" data-testid="invite-accept-page">
      <div className="text-center mb-6">
        <div className="w-14 h-14 bg-[#059669] mx-auto flex items-center justify-center mb-4">
          <Building2 className="w-7 h-7 text-white" />
        </div>
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252] mb-1">§ Office Pack invite</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter">Join {invite.business_name}</h1>
        <p className="text-[#525252] mt-3">
          <span className="font-bold text-[#0A0A0A]">{invite.inviter_name || "A teammate"}</span> invited{" "}
          <span className="mono font-bold">{invite.email}</span> to the {invite.plan_label} plan.
          Every order you place will auto-settle from the company wallet —
          {" "}{invite.discount_pct}% off, no STK needed.
        </p>
      </div>

      <div className="bg-white border-2 border-[#0A0A0A] p-6">
        {!user ? (
          <>
            <p className="text-sm text-[#525252] mb-4">
              Sign in (or sign up) with <span className="mono font-bold text-[#0A0A0A]">{invite.email}</span> to accept.
            </p>
            <div className="flex flex-col gap-2">
              <Link
                to={`/login?next=/invite/${token}`}
                data-testid="invite-login-cta"
                className="block text-center bg-[#059669] text-white px-6 py-3 text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#047857]"
              >
                Sign in
              </Link>
              <Link
                to={`/signup?next=/invite/${token}&email=${encodeURIComponent(invite.email)}`}
                data-testid="invite-signup-cta"
                className="block text-center border-2 border-[#0A0A0A] text-[#0A0A0A] px-6 py-3 text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#0A0A0A] hover:text-white"
              >
                Create an account
              </Link>
            </div>
          </>
        ) : !emailMatches ? (
          <>
            <div className="flex items-start gap-2 text-sm text-[#92400E] mb-4">
              <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p>
                You&apos;re signed in as <span className="mono font-bold">{user.email}</span>, but this invite is for
                {" "}<span className="mono font-bold">{invite.email}</span>. Sign out and use that account to accept.
              </p>
            </div>
            <Link
              to="/login"
              data-testid="invite-switch-cta"
              className="block text-center border-2 border-[#DC2626] text-[#DC2626] px-6 py-3 text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#DC2626] hover:text-white"
            >
              Switch account
            </Link>
          </>
        ) : (
          <button
            onClick={acceptInvite}
            disabled={accepting}
            data-testid="invite-accept-btn"
            className="w-full bg-[#059669] text-white px-6 py-4 text-sm uppercase tracking-[0.2em] font-bold hover:bg-[#047857] disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            {accepting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Accept &amp; join {invite.business_name}
          </button>
        )}
      </div>
    </div>
  );
}
